import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useParams } from 'react-router-dom';
import { useClinicDashboard } from '../context/ClinicDashboardContext';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import CategoryBar from '../components/CategoryBar';
import DateFilter from '../components/DateFilter';
import EmptyState from '../components/EmptyState';
import api from '../../../../services/api';

const PIE_COLORS = ['#0f4c5c', '#f97316'];

const InpatientClinicalManagement = ({ onBack }) => {
    const { clinicId } = useParams();
    const { sharedMonthYear } = useClinicDashboard();
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        month: sharedMonthYear.month,
        year: sharedMonthYear.year,
        start_date: '',
        end_date: ''
    });
    const [data, setData] = useState({
        kpis: [],
        speciesBars: [],
        inpatientData: [],
        dischargePie: [],
        currentDischarges: []
    });

    useEffect(() => {
        const fetchInpatientData = async () => {
            try {
                setLoading(true);
                const params = new URLSearchParams();
                params.append('year', filters.year);
                if (filters.month !== undefined && filters.month !== null) {
                    params.append('month', filters.month + 1);
                }
                const response = await api.get(`/api/v1/clinics/${clinicId}/dashboard/inpatient?${params.toString()}`);
                if (response.data?.status === 'success') {
                    setData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching inpatient clinical management data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchInpatientData();
        }
    }, [clinicId, filters]);

    if (loading) {
        return <div className="p-4">Loading inpatient management report...</div>;
    }

    const monthName = new Date(filters.year, filters.month, 1).toLocaleDateString('en-US', { month: 'long' });

    const filterMonthlyChartData = (chartData, year) => {
        const now = new Date();
        if (year < now.getFullYear()) return chartData;
        return chartData.slice(0, now.getMonth() + 1);
    };

    const inpatientData = filterMonthlyChartData(data.inpatientData, filters.year);

    const hasSpeciesData = data.speciesBars.some(b => b.value > 0);
    const hasDischargeData = data.dischargePie.some(d => d.value > 0);
    const hasDischargeList = data.currentDischarges.length > 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
            <ViewHeader
                title="Inpatient Clinical Management"
                subtitle={`${monthName} ${filters.year}`}
                onBack={onBack}
                filter={
                    <DateFilter
                        mode="monthYear"
                        value={filters}
                        onChange={setFilters}
                    />
                }
            />

            {/* Top row — equal height */}
            <div style={{ display: 'flex', gap: '16px' }}>
                <div className="clinic-dashboard-section-card" style={{ flex: 1, animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Key metrics</div>
                    <div className="clinic-dashboard-kpi-row">
                        {data.kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>

                <div className="clinic-dashboard-section-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Species breakdown</div>
                    {hasSpeciesData ? (
                        <div className="clinic-dashboard-catbar-list large">
                            {data.speciesBars.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                        </div>
                    ) : (
                        <EmptyState message="No species data for this period" />
                    )}
                </div>
            </div>

            {/* Bottom row — line chart left, pie + list side-by-side right */}
            <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>
                <div className="clinic-dashboard-section-card" style={{ flex: 1.2, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Inpatient over time</div>
                    <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '200px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={inpatientData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Line animationBegin={600} type="monotone" dataKey="value" stroke="#0f4c5c" strokeWidth={2} dot={{ r: 4, fill: '#0f4c5c' }} activeDot={{ r: 6 }} name="inpatient" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Right side: discharged pie + current discharges side by side */}
                <div style={{ display: 'flex', gap: '16px', flex: 0.8, minHeight: 0 }}>
                    <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '240ms' }}>
                        <div className="clinic-dashboard-section-title">Discharged</div>
                        {hasDischargeData ? (
                            <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '220px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <ResponsiveContainer width="100%" height="85%">
                                    <PieChart>
                                        <Pie
                                            data={data.dischargePie}
                                            outerRadius={85}
                                            paddingAngle={2}
                                            dataKey="value"
                                            stroke="#fff"
                                            strokeWidth={2}
                                        >
                                            {data.dischargePie.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="clinic-dashboard-legend">
                                    {data.dischargePie.map((entry, index) => (
                                        <div key={index} className="clinic-dashboard-legend-item">
                                            <span className="clinic-dashboard-legend-dot" style={{ backgroundColor: PIE_COLORS[index] }} />
                                            <span className="clinic-dashboard-legend-label">{entry.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <EmptyState message="No discharge data for this period" />
                        )}
                    </div>

                    <div className="clinic-dashboard-section-card" style={{ flex: 1, alignSelf: 'flex-start', animationDelay: '320ms' }}>
                        <div className="clinic-dashboard-section-title">Current discharges</div>
                        {hasDischargeList ? (
                            <div className="clinic-dashboard-discharge-list">
                                {data.currentDischarges.map((row, i) => (
                                    <div key={i} className="clinic-dashboard-discharge-item">
                                        <span className="clinic-dashboard-discharge-name">{row.pet}</span>
                                        <span className="clinic-dashboard-discharge-time">{row.time}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <EmptyState message="No discharges recorded for this period" />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InpatientClinicalManagement;
